import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Auth } from 'aws-amplify';

import { Formik, Form } from 'formik';
import { object, string } from 'yup';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { FormButtonWrapper } from 'profileManagement/FormButtonWrapper';
import { Title } from 'components/layout/Title';
import RequiredFieldIndicator from 'components/layout/RequiredFieldIndicator';
import { CallerLegalAgreement } from 'legal/CallerLegalAgreement';
import { mapCognitoErrors } from 'appRoot/auth/mapCognitoErrors';
import { AuthContext } from 'appRoot/auth/AuthContext';
import { FormikBasicField } from 'util-components/formik/FormikBasicField';
import { createBreakpoint } from 'util-components/util/createBreakpoint';
import { LoadingOverlay } from 'util-components/layout/LoadingOverlay';
import { LoginFailed } from './LoginFailed';

const TimeoutMessage = () => {
   const authContext = useContext(AuthContext);
   const activityStatus = authContext?.activityStatus;
   return activityStatus === 'timeout' ? (
      <div className="alert alert-warning pl-3 mt-5">
         You have been logged out due to inactivity.
      </div>
   ) : null;
};

const LoginWrapper = styled.div`
   max-width: 25rem;
   margin: 0 auto;
`;

const Error = styled.div`
   ${({ theme }) => createBreakpoint('above', theme.breakpoints.sm)} {
      margin-left: -6rem;
      margin-right: -6rem;
   }
`;

const LegalMessage = styled.div`
   overflow: auto;

   ::-webkit-scrollbar-thumb {
      background: #856404;
   }
`;

const validationSchema = object({
   username: string().required().label('Username'),
   password: string().required().label('Password'),
});

const LoginForm = ({ onSubmit }) => (
   <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={{ username: '', password: '' }}
      validateOnMount
   >
      {({ isValid }) => (
         <Form>
            <RequiredFieldIndicator />
            <FormikBasicField label="Username" name="username" required />
            <FormikBasicField
               label="Password"
               name="password"
               required
               type="password"
            />

            <FormButtonWrapper>
               <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={!isValid}
                  id="login-btn"
               >
                  Login
               </button>
            </FormButtonWrapper>
         </Form>
      )}
   </Formik>
);

const ErrorText = ({ error }) => {
   if (
      error === 'User is disabled.' ||
      error ===
         'PreAuthentication failed with error Your account is currently locked.'
   ) {
      return <LoginFailed />;
   }
   return <span>{error}</span>;
};

ErrorText.propTypes = {
   error: PropTypes.string.isRequired,
};

LoginForm.propTypes = { onSubmit: PropTypes.func.isRequired };

const Login = () => {
   const authContext = useContext(AuthContext);
   const { setAuthState } = authContext || {};

   const [error, setError] = useState(null);
   const [loading, setLoading] = useState(false);

   const handleFormSubmit = async ({ username, password }) => {
      setError(null);
      setLoading(true);

      try {
         const user = await Auth.signIn(username, password);

         // handle challenges
         if (
            user.challengeName === 'SMS_MFA' ||
            user.challengeName === 'SOFTWARE_TOKEN_MFA'
         ) {
            setAuthState({ state: 'mfaChallenge', user });
         } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            setAuthState({ state: 'changePasswordRequired', user });
         } else {
            setAuthState({ state: 'loggedIn' });
         }
      } catch (err) {
         setError(mapCognitoErrors(err?.message || err));
         setLoading(false);
      }
   };

   return (
      <div className="container">
         <LoginWrapper className="pb-5">
            <Title title="Login" />
            <h1 className="mt-5 mb-3">Login to RND</h1>

            {!error && !loading && <TimeoutMessage />}

            {error && (
               <Error className="alert alert-danger pl-3">
                  <ErrorText error={error} />
               </Error>
            )}

            <LoginForm onSubmit={handleFormSubmit} />

            <p className="mt-5">
               <Link to="/forgot-username">Forgot Username?</Link>
            </p>
            <p>
               <Link to="/forgot-password">Forgot Password?</Link>
            </p>

            {loading && <LoadingOverlay />}

            <LegalMessage className="alert alert-warning pl-3 mt-5 mb-0">
               <small>
                  <strong>
                     By Logging in to the RND you are agreeing to the following:
                  </strong>{' '}
                  {CallerLegalAgreement}
               </small>
            </LegalMessage>
         </LoginWrapper>
      </div>
   );
};

export { Login };
